<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useEventListener } from "@vueuse/core";
import axios from "axios";
import urls from "@/urls.js";
import { useSearchStore } from "@/searchStore";
import { useFavoritesStore } from "@/favoritesStore";
import MobileMenu from "./MobileMenu.vue";
import { Search, Menu, Heart } from "lucide-vue-next";
import fallbackLogo from "@/images/Logos/LOGO.png";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const searchStore = useSearchStore();
const favoritesStore = useFavoritesStore();

const menuItems = [
  { name: "Inicio", to: "/" },
  { name: "Compra tu auto", to: "/catalogo/0/0/0" },
  { name: "Favoritos", to: "/favoritos" },
  { name: "Contacto", to: "/contacto" },
  { name: "Nosotros", to: "/recomendaciones" },
  // { name: "Blog", to: "/blog" },
];

const SitiosWeb = ref({});
const scrolled = ref(false);
const menuOpen = ref(false);
const menuBtnRef = ref(null);

const searchQuery = computed({
  get: () => searchStore.query,
  set: (val) => {
    searchStore.query = val;
    searchStore.searchOnlyParameter = false;
  },
});

const submitSearch = () => {
  router.push("/catalogo/0/0/0");
  menuOpen.value = false;
};

const logoSrc = computed(() => {
  if (SitiosWeb.value?.urlfoto && SitiosWeb.value?.logo) {
    return SitiosWeb.value.urlfoto + urls.URL_SITIOSWEB + SitiosWeb.value.logo;
  }
  return fallbackLogo;
});

const isActive = (to) => {
  if (to === "/") return route.path === "/";
  return route.path.startsWith(to.split("/").slice(0, 2).join("/"));
};

const GetSitioWeb = async () => {
  try {
    const { data } = await axios.get("SitioWeb");
    SitiosWeb.value = data[0];
  } catch (error) {
    toast.error("Error al cargar datos:" + error, { position: "top-center" });
  }
};

const SetWhatsApp = () => {
  window.open("https://api.whatsapp.com/send?phone=4494551588", "_blank");
};

onMounted(async () => {
  await GetSitioWeb();
});

useEventListener(
  window,
  "scroll",
  () => {
    scrolled.value = window.scrollY > 8;
  },
  { passive: true }
);

const closeMenuAndRestoreFocus = () => {
  menuOpen.value = false;
};
</script>

<template>
  <header class="c4u-navbar" :class="{ 'is-scrolled': scrolled }">
    <div class="c4u-navbar__inner">
      <RouterLink to="/" class="c4u-navbar__brand" aria-label="CAR4U — Ir al inicio">
        <img :src="logoSrc" alt="CAR4U" class="c4u-navbar__logo" width="120" height="40" />
      </RouterLink>

      <nav class="c4u-navbar__nav" aria-label="Navegación principal">
        <RouterLink
          v-for="item in menuItems"
          :key="item.to"
          :to="item.to"
          class="c4u-navbar__link"
          :class="{ 'is-active': isActive(item.to) }"
        >
          {{ item.name }}
        </RouterLink>
      </nav>

      <form class="c4u-navbar__search" role="search" @submit.prevent="submitSearch">
        <Search class="c4u-navbar__search-icon" :size="18" :stroke-width="1.75" aria-hidden="true" />
        <label for="c4u-search" class="c4u-visually-hidden">Buscar por marca, modelo, año o color</label>
        <input
          id="c4u-search"
          v-model="searchQuery"
          type="search"
          placeholder="Buscar marca, modelo, año…"
          class="c4u-navbar__search-input"
        />
      </form>

      <div class="c4u-navbar__actions">
        <RouterLink to="/favoritos" class="c4u-navbar__favorite" aria-label="Ver favoritos">
          <Heart :size="20" :stroke-width="1.75" aria-hidden="true" />
          <span v-if="favoritesStore.count > 0" class="c4u-navbar__favorite-badge">{{ favoritesStore.count }}</span>
        </RouterLink>

        <button
          ref="menuBtnRef"
          type="button"
          class="c4u-navbar__menu-btn"
          aria-label="Abrir menú"
          aria-haspopup="dialog"
          :aria-expanded="menuOpen"
          @click="menuOpen = true"
        >
          <Menu :size="26" :stroke-width="1.75" aria-hidden="true" />
        </button>
      </div>
    </div>

    <MobileMenu
      v-model="menuOpen"
      :menu-items="menuItems"
      @whatsapp="SetWhatsApp"
    />
  </header>
</template>

<style scoped>
.c4u-navbar {
  position: sticky;
  top: 0;
  z-index: var(--c4u-z-nav);
  background: var(--c4u-black);
  border-bottom: 1px solid transparent;
  transition: border-color var(--c4u-dur-base) var(--c4u-ease-out),
    box-shadow var(--c4u-dur-base) var(--c4u-ease-out);
}

.c4u-navbar.is-scrolled {
  border-bottom-color: var(--c4u-border-on-dark);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.c4u-navbar__inner {
  max-width: var(--c4u-container);
  margin-inline: auto;
  padding-inline: var(--c4u-gutter);
  height: var(--c4u-nav-height-mobile);
  display: flex;
  align-items: center;
  gap: var(--c4u-space-md);
}

.c4u-navbar__brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.c4u-navbar__logo {
  height: 34px;
  width: auto;
  object-fit: contain;
}

.c4u-navbar__nav {
  display: none;
}

.c4u-navbar__search {
  display: none;
}

.c4u-navbar__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
}

.c4u-navbar__favorite {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border-radius: var(--c4u-radius-md);
  color: var(--c4u-text-on-dark);
  transition: background-color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-navbar__favorite:hover {
  background: rgba(255, 255, 255, 0.08);
}

.c4u-navbar__favorite:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 2px;
}

.c4u-navbar__favorite-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding-inline: 3px;
  background: var(--c4u-green);
  color: var(--c4u-white);
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
}

.c4u-navbar__menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  border-radius: var(--c4u-radius-md);
  color: var(--c4u-text-on-dark);
  cursor: pointer;
  transition: background-color var(--c4u-dur-fast) var(--c4u-ease-out);
}

.c4u-navbar__menu-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

.c4u-navbar__menu-btn:focus-visible {
  outline: 2px solid var(--c4u-focus-ring);
  outline-offset: 2px;
}

@media (min-width: 1024px) {
  .c4u-navbar__inner {
    height: var(--c4u-nav-height);
    gap: var(--c4u-space-lg);
  }

  .c4u-navbar__logo {
    height: 42px;
  }

  .c4u-navbar__nav {
    display: flex;
    align-items: center;
    gap: var(--c4u-space-lg);
    margin-left: var(--c4u-space-md);
  }

  .c4u-navbar__link {
    position: relative;
    color: var(--c4u-text-on-dark-muted);
    text-decoration: none;
    font-family: var(--c4u-font-display);
    font-weight: 500;
    font-size: var(--c4u-fs-label);
    letter-spacing: 0.02em;
    padding-block: var(--c4u-space-xs);
    transition: color var(--c4u-dur-fast) var(--c4u-ease-out);
  }

  .c4u-navbar__link::after {
    content: "";
    position: absolute;
    left: 0;
    right: 100%;
    bottom: 0;
    height: 2px;
    background: var(--c4u-green-light);
    transition: right var(--c4u-dur-base) var(--c4u-ease-out);
  }

  .c4u-navbar__link:hover,
  .c4u-navbar__link.is-active {
    color: var(--c4u-text-on-dark);
  }

  .c4u-navbar__link:hover::after,
  .c4u-navbar__link.is-active::after {
    right: 0;
  }

  .c4u-navbar__link:focus-visible {
    outline: 2px solid var(--c4u-focus-ring);
    outline-offset: 4px;
  }

  .c4u-navbar__search {
    display: flex;
    align-items: center;
    gap: var(--c4u-space-xs);
    margin-left: auto;
    padding: 0 var(--c4u-space-sm);
    height: 42px;
    width: clamp(200px, 22vw, 280px);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid var(--c4u-border-on-dark);
    border-radius: 999px;
    transition: border-color var(--c4u-dur-fast) var(--c4u-ease-out),
      background-color var(--c4u-dur-fast) var(--c4u-ease-out);
  }

  .c4u-navbar__search:focus-within {
    border-color: var(--c4u-green-light);
    background: rgba(255, 255, 255, 0.12);
  }

  .c4u-navbar__search-icon {
    color: var(--c4u-text-on-dark-muted);
    flex-shrink: 0;
  }

  .c4u-navbar__search-input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: var(--c4u-text-on-dark);
    font-family: var(--c4u-font-body);
    font-size: var(--c4u-fs-label);
  }

  .c4u-navbar__search-input::placeholder {
    color: var(--c4u-text-on-dark-muted);
  }

  .c4u-navbar__actions {
    margin-left: 0;
  }

  .c4u-navbar__menu-btn {
    display: none;
  }
}
</style>
